import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import { MultiSelect } from 'react-multi-select-component';
import { useSelectedProduct } from '../contexts/SelectedProductContext';
import DashboardHelpInfoTab from './Dashboard-HelpInfo-Tab';
import DashboardExportTab from './Dashboard-Export-Tab';
import TestMap from './TestMap';
import DashboardAnalysisTabLatest from './DashboardAnalysisTabLatest';
import envConfig from '../envConfig';
import expandIcon from '../assets/images/about-Info.svg';
import collapseIcon from '../assets/images/close.svg';
import bgImage from '../assets/images/layers.png';

function Dashboard(props) {
    const [isOpen, setIsOpen] = useState(true);
    const [currentTab, setCurrentTab] = useState('layer');
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState(1);
    const [selectedHazardIds, setSelectedHazardIds] = useState([]);
    const [isTemporal, setIsTemporal] = useState(false);
    const [showAggregated, setShowAggregated] = useState(true);
    const [aggregatedSingleHazard, setAggregatedSingleHazard] = useState(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [hazards, setHazards] = useState([]);
    const [countries, setCountries] = useState([]);
    const [riverBasins, setRiverBasins] = useState([]);
    const [loading, setLoading] = useState(0);
    const [lookUpLoading, setLookUpLoading] = useState(0);
    const [hazardLimit, setHazardLimit] = useState(false);
    const [timebar, setTimebar] = useState(false);
    const [showHazard, setShowHazard] = useState(true);
    const [showRiverBasin, setShowRiverBasin] = useState(false);
    const [showRivers, setShowRivers] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedBasins, setSelectedBasins] = useState([]);
    const [selectedHazardMap, setSelectedHazardMap] = useState(null);
    const [riverGeoJson, setRiverGeoJson] = useState(null);
    const [mapGeojsonLegend, setMapGeojsonLegend] = useState(null);
    const [selectedYear, setSelectedYear] = useState(1990);
    const [mapGeoJsonData, setMapGeoJsonData] = useState(null);
    const [mapSelectedCountries, setMapSelectedCountries] = useState([]);
    const [isFetchingLegend, setIsFetchingLegend] = useState(false);
    const years = [
        1990, 1991, 1992, 1993, 1994, 1995, 1996, 1997, 1998, 1999, 2000,
        2001, 2002, 2003, 2004, 2005, 2006, 2007, 2008, 2009, 2010,
        2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020
    ];
    const [geoJsonData, setGeoJsonData] = useState(null);
    const navigate = useNavigate();
    const { selectedProduct, setSelectedProduct, isFromUI, setIsFromUI } = useSelectedProduct();
    const location = useLocation();

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        const path = location.pathname;
        const productMatch = path.match(/^\/(product[1-4])\/dashboard$/);
        const newProduct = productMatch ? productMatch[1] : 'product1';

        if (newProduct !== selectedProduct && !isFromUI) {
            console.log(`URL Sync: Setting product to ${newProduct} from URL ${path}`);
            setSelectedProduct(newProduct);
            setActiveTab(1);
            setCurrentTab('layer');
        }
    }, [location.pathname, selectedProduct, setSelectedProduct, isFromUI, setIsFromUI]);

    const toggleSidenav = () => {
        setIsOpen(!isOpen);
    };

    const toggleContent = () => {
        setIsExpanded(!isExpanded);
    };

    const handleProductSelect = (product) => {
        console.log(`Dropdown: Changing product to ${product}`);
        setIsFromUI(true);
        setSelectedProduct(product);
        setActiveTab(1);
        setCurrentTab('layer');
        navigate(`/${product}/dashboard`, { replace: false });
        setTimeout(() => setIsFromUI(false), 0);
    };

    const handleSliderValueChange = (value) => {
        setSelectedYear(value);
    };

    const handleTabClick = (tab) => {
        if (tab !== activeTab) {
            setActiveTab(tab);
            if (tab === 4 || tab === 5) {
                setCurrentTab(null);
            } else {
                setCurrentTab('layer');
            }
        }
    };

    const handleResize = () => {
        setHeight(window.innerHeight);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/adm0`);
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const result = await response.json();
                setCountries(result.data || []);
                const allCountries = result.data.map((country) => ({
                    value: country.adm0_code,
                    label: country.adm0_name,
                }));
                setSelectedCountries(allCountries);
                setMapSelectedCountries(allCountries);
            } catch (error) {
                console.error('Failed to fetch countries:', error);
            }
        };

        const fetchGeojson = async () => {
            try {
                const response = await fetch('/data/africa.geojson');
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                return await response.json();
            } catch (error) {
                console.error('Failed to fetch GeoJSON:', error);
                throw error;
            }
        };

        const fetchHazards = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/hazards`);
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const result = await response.json();
                return { hazards: result.data || [], allHazardIds: [2, 3, 5, 8] };
            } catch (error) {
                console.error('Failed to fetch hazards:', error);
                throw error;
            }
        };

        const fetchBasins = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/major_basins`);
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const result = await response.json();
                const allBasins = result.data.map((basin) => ({
                    value: basin.basin_code,
                    label: basin.basin,
                }));
                return { basins: result.data || [], allRiverBasins: allBasins };
            } catch (error) {
                console.error('Failed to fetch basins:', error);
                throw error;
            }
        };

        const fetchData = async () => {
            try {
                setLookUpLoading(true);
                const [geoJson, hazardsData, basinData] = await Promise.all([
                    fetchGeojson(),
                    fetchHazards(),
                    fetchBasins(),
                    fetchCountries()
                ]);
                setGeoJsonData(geoJson);
                setMapGeoJsonData(geoJson);
                setHazards(hazardsData.hazards);
                setSelectedHazardIds(hazardsData.allHazardIds);
                setRiverBasins(basinData.basins);
                setSelectedBasins(basinData.allRiverBasins);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLookUpLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (geoJsonData) {
            handleHazardSelect(selectedHazardIds, selectedCountries);
            getBasinGeojson(selectedBasins);
        }
    }, [geoJsonData]);

    const handleApply = () => {
        if (selectedHazardIds.length > 4) {
            setHazardLimit(true);
            return;
        }
        if (selectedHazardIds.length === 1) {
            setTimebar(true);
            setIsTemporal(true);
        } else {
            setTimebar(false);
            setIsTemporal(false);
        }
        setMapSelectedCountries(selectedCountries);
        setMapGeoJsonData(geoJsonData);
        handleHazardSelect(selectedHazardIds, selectedCountries);
        getBasinGeojson(selectedBasins);
    };

    const handleCheckboxChange = (event, hazardId) => {
        const isChecked = event.target.checked;
        const newSelectedHazardIds = isChecked
            ? [...selectedHazardIds, hazardId]
            : selectedHazardIds.filter((id) => id !== hazardId);
        setSelectedHazardIds(newSelectedHazardIds);
    };

    const getBasinGeojson = async (selectedIds) => {
        setLoading(prev => prev + 1);
        let payload = {
            basin_codes: selectedIds.map(item => item.value),
            include_rivers: true
        };
        let url = `/basins/major_basins`;
        try {
            const response = await fetch(`${envConfig.API_URL}${url}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            const result = await response.json();
            setRiverGeoJson(result.data);
        } catch (error) {
            console.error('Failed to fetch basin GeoJSON:', error);
        } finally {
            setLoading(prev => (prev > 0 ? prev - 1 : 0));
        }
    };

    const handleHazardSelect = async (selectedIds, countriesForPayload) => {
        setLoading((prev) => prev + 1);
        setSelectedHazardMap(null);
        let urls = [];
        let payloads = [];
        if (!isTemporal) {
            let payloadMultiHazard = {
                purpose: 'map',
                admX: 2,
                adm0_code: countriesForPayload.map((item) => item.value),
                adm1_code: [],
                hazard_ids: selectedIds,
                geometry_required: false,
            };
            urls.push(`${envConfig.API_URL}/hazards/multihazard`);
            payloads.push(payloadMultiHazard);
        } else {
            let payloadTemporalFalse = {
                purpose: 'map',
                admX: 2,
                adm0_code: countriesForPayload.map((item) => item.value),
                adm1_code: [],
                hazard_id: selectedIds[0],
                loss_id: null,
                dataset: 'occurrences',
                total: false,
                years: years,
                geometry_required: false,
            };
            let payloadTemporalTrue = {
                purpose: 'map',
                admX: 2,
                adm0_code: countriesForPayload.map((item) => item.value),
                adm1_code: [],
                hazard_id: selectedIds[0],
                loss_id: null,
                dataset: 'occurrences',
                total: true,
                years: [],
                geometry_required: false,
            };
            urls.push(`${envConfig.API_URL}/hazards/timeseries`);
            payloads.push(payloadTemporalFalse);
            urls.push(`${envConfig.API_URL}/hazards/timeseries`);
            payloads.push(payloadTemporalTrue);
        }
        try {
            const responses = await Promise.all(
                urls.map((url, index) =>
                    fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payloads[index]),
                    })
                )
            );
            const results = await Promise.all(
                responses.map(async (response, index) => {
                    if (!response.ok) {
                        const errorText = await response.text();
                        throw new Error(`Error fetching hazard data from ${urls[index]}: ${response.status} - ${errorText}`);
                    }
                    return response.json();
                })
            );
            let multiHazardData = !isTemporal ? results[0] : null;
            let temporalDataFalse = isTemporal ? results[0] : null;
            let temporalDataTrue = isTemporal ? results[1] : null;
            if (multiHazardData) {
                let multiGeoJsonData = JSON.parse(JSON.stringify(mapGeoJsonData));
                for (let i = 0; i < multiGeoJsonData.features.length; i++) {
                    for (let j = 0; j < multiHazardData.data.geojson.length; j++) {
                        if (multiGeoJsonData.features[i].properties.ADM2_CODE === multiHazardData.data.geojson[j].ADM2_CODE) {
                            multiGeoJsonData.features[i].properties.color = multiHazardData.data.geojson[j].color ?? '#f0f0f0';
                            multiGeoJsonData.features[i].properties.symbol = multiHazardData.data.geojson[j].symbol;
                            multiGeoJsonData.features[i].properties.temporal = multiHazardData.data.geojson[j].temporal;
                            const country = countries.find((item) => item.adm0_code === multiGeoJsonData.features[i].properties.ADM0_CODE);
                            multiGeoJsonData.features[i].properties.country = country?.adm0_name ?? 'N/A';
                        }
                    }
                }
                multiHazardData.data.geojson = multiGeoJsonData;
                setSelectedHazardMap(multiHazardData.data);
            }
            if (isTemporal && temporalDataFalse && temporalDataTrue) {
                let multiGeoJsonData = JSON.parse(JSON.stringify(mapGeoJsonData));
                for (let i = 0; i < multiGeoJsonData.features.length; i++) {
                    multiGeoJsonData.features[i].properties.total = 0;
                    for (let j = 0; j < temporalDataTrue.data.geojson.length; j++) {
                        if (multiGeoJsonData.features[i].properties.ADM2_CODE === temporalDataTrue.data.geojson[j].ADM2_CODE) {
                            multiGeoJsonData.features[i].properties.color = temporalDataTrue.data.geojson[j].color ?? '#f0f0f0';
                            multiGeoJsonData.features[i].properties.symbol = temporalDataTrue.data.geojson[j].symbol;
                            multiGeoJsonData.features[i].properties.total = temporalDataTrue.data.geojson[j].total;
                        }
                    }
                }
                temporalDataTrue.data.geojson = multiGeoJsonData;
                temporalDataTrue.data = await getMapData(temporalDataTrue.data.geojson, temporalDataTrue.data.color_scale);
                setSelectedHazardMap(temporalDataTrue.data);
                setAggregatedSingleHazard(temporalDataTrue.data);
                let temporalGeoJsonData = JSON.parse(JSON.stringify(mapGeoJsonData));
                for (let i = 0; i < temporalGeoJsonData.features.length; i++) {
                    let feature = temporalGeoJsonData.features[i];
                    if (!feature.properties) {
                        feature.properties = {};
                    }
                    feature.properties.temporal = {};
                    for (let j = 0; j < temporalDataFalse.data.geojson.length; j++) {
                        let match = temporalDataFalse.data.geojson[j];
                        if (feature.properties.ADM2_CODE === match.ADM2_CODE) {
                            feature.properties.color = match.color ?? '#f0f0f0';
                            feature.properties.symbol = match.symbol;
                            feature.properties.temporal = { ...feature.properties.temporal, ...match.temporal };
                            break;
                        }
                    }
                }
                temporalDataFalse.data = JSON.parse(JSON.stringify({ geojson: temporalGeoJsonData }));
                setMapGeojsonLegend(temporalDataFalse.data);
                console.log('Dashboard: mapGeojsonLegend set:', temporalDataFalse.data);
            }
        } catch (error) {
            console.error('Failed to fetch hazard data:', error);
        } finally {
            setLoading((prev) => (prev > 0 ? prev - 1 : 0));
        }
    };

    const getMapData = async (result_data_geojson, colorPallet) => {
        let data = {
            geojson: [],
            legend: []
        };
        let responseColorPallet = getColorPallet();
        colorPallet = responseColorPallet[selectedHazardIds[0]];
        result_data_geojson.features.forEach((feature) => {
            const total =
                feature.properties.temporal && Object.keys(feature.properties.temporal).length > 0
                    ? feature.properties.temporal[selectedYear] ?? 0
                    : feature.properties.total ?? 0;
            feature.properties.color = colorPallet[total]?.color ?? '#f0f0f0';
            const country = countries.find(item => item.adm0_code === feature.properties.ADM0_CODE);
            feature.properties.country = country.adm0_name ?? 'N/A';
        });
        data.geojson = result_data_geojson;
        data.legend = Object.values(colorPallet);
        return data;
    };

    const getColorPallet = () => {
        return {
            2: { // flood
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#b5caff", "intensity": 1 },
                2: { "color": "#abc0ff", "intensity": 2 },
                3: { "color": "#9db8ff", "intensity": 3 },
                4: { "color": "#95b0ff", "intensity": 4 },
                5: { "color": "#87a7fe", "intensity": 5 },
                6: { "color": "#79a0ff", "intensity": 6 },
                7: { "color": "#6e97ff", "intensity": 7 },
                8: { "color": "#6191ff", "intensity": 8 },
                9: { "color": "#5486ff", "intensity": 9 },
                10: { "color": "#4580fe", "intensity": 10 },
                11: { "color": "#347afe", "intensity": 11 },
                12: { "color": "#1c73ff", "intensity": 12 },
                13: { "color": "#005adf", "intensity": 13 },
                14: { "color": "#0253d6", "intensity": 14 },
                15: { "color": "#014fcb", "intensity": 15 },
                16: { "color": "#024dc4", "intensity": 16 },
                17: { "color": "#0245ba", "intensity": 17 },
                18: { "color": "#0242b0", "intensity": 18 },
                19: { "color": "#033ea6", "intensity": 19 },
                20: { "color": "#013b9e", "intensity": 20 },
                23: { "color": "#003593", "intensity": 23 },
                24: { "color": "#01338c", "intensity": 24 },
                25: { "color": "#022c82", "intensity": 25 },
                26: { "color": "#012a7a", "intensity": 26 },
                28: { "color": "#002573", "intensity": 28 }
            },
            3: { // drought
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#ffeb08", "intensity": 1 },
                2: { "color": "#ffd20a", "intensity": 2 },
                3: { "color": "#ffba0d", "intensity": 3 },
                4: { "color": "#fda50f", "intensity": 4 },
                5: { "color": "#f07e1b", "intensity": 5 },
                6: { "color": "#e36125", "intensity": 6 },
                7: { "color": "#d6462c", "intensity": 7 },
                8: { "color": "#c83233", "intensity": 8 },
                9: { "color": "#b32825", "intensity": 9 },
                11: { "color": "#9c1a18", "intensity": 11 },
                12: { "color": "#88120e", "intensity": 12 },
                13: { "color": "#720000", "intensity": 13 }
            },
            4: { // earthquake
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#ffa600", "intensity": 1 },
                2: { "color": "#ff5100", "intensity": 2 },
                3: { "color": "#fe1100", "intensity": 3 },
                4: { "color": "#c80000", "intensity": 4 },
                5: { "color": "#800000", "intensity": 5 }
            },
            5: { // epidemic
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#e0ce00", "intensity": 1 },
                2: { "color": "#e3bd00", "intensity": 2 },
                3: { "color": "#e3ae00", "intensity": 3 },
                4: { "color": "#e69d00", "intensity": 4 },
                5: { "color": "#e78e00", "intensity": 5 },
                6: { "color": "#e67f00", "intensity": 6 },
                7: { "color": "#e36a01", "intensity": 7 },
                8: { "color": "#e25b01", "intensity": 8 },
                9: { "color": "#e14b00", "intensity": 9 },
                10: { "color": "#df3400", "intensity": 10 },
                14: { "color": "#dd1e01", "intensity": 14 }
            },
            6: { // extreme temperature
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#ffba0d", "intensity": 1 },
                2: { "color": "#e36125", "intensity": 2 },
                3: { "color": "#b32825", "intensity": 3 },
                4: { "color": "#720000", "intensity": 4 }
            },
            7: { // landslide
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#ffebb0", "intensity": 1 },
                2: { "color": "#ffd380", "intensity": 2 },
                3: { "color": "#e69800", "intensity": 3 },
                4: { "color": "#f57b7a", "intensity": 4 }
            },
            8: { // storm
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#fff321", "intensity": 1 },
                2: { "color": "#ffe733", "intensity": 2 },
                3: { "color": "#fed940", "intensity": 3 },
                4: { "color": "#fecc49", "intensity": 4 },
                5: { "color": "#ffc154", "intensity": 5 },
                6: { "color": "#ffb55e", "intensity": 6 },
                7: { "color": "#ffa768", "intensity": 7 },
                8: { "color": "#ff9a70", "intensity": 8 },
                9: { "color": "#ff7f7e", "intensity": 9 },
                10: { "color": "#ff5500", "intensity": 10 }
            },
            9: { // volcanic
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#e36125", "intensity": 1 },
                2: { "color": "#720000", "intensity": 2 }
            },
            10: { // wildfire
                0: { "color": "#f0f0f0", "intensity": 0 },
                1: { "color": "#ffa77f", "intensity": 1 },
                2: { "color": "#e54c00", "intensity": 2 }
            }
        };
    };

    useEffect(() => {
        const updateHazardMap = async () => {
            if (!isTemporal) {
                if (selectedHazardMap) {
                    setSelectedHazardMap({ ...selectedHazardMap });
                    console.log('Dashboard: Non-temporal hazard map retained:', selectedHazardMap);
                    return;
                }
            }
            if (showAggregated && aggregatedSingleHazard) {
                setSelectedHazardMap({ ...aggregatedSingleHazard });
                console.log('Dashboard: Showing aggregated hazard map for year:', selectedYear);
                return;
            }
            if (isTemporal) {
                if (mapGeojsonLegend) {
                    console.log('Dashboard: Updating hazard map for year:', selectedYear);
                    const data = await getMapData(mapGeojsonLegend.geojson, mapGeojsonLegend.color_scale);
                    setSelectedHazardMap({ ...data });
                    console.log('Dashboard: Updated hazard map for year:', selectedYear, data);
                } else if (!isFetchingLegend) {
                    console.log('Dashboard: mapGeojsonLegend not available, fetching hazard data for year:', selectedYear);
                    setIsFetchingLegend(true);
                    await handleHazardSelect(selectedHazardIds, mapSelectedCountries);
                } else {
                    console.log('Dashboard: Waiting for mapGeojsonLegend to be fetched for year:', selectedYear);
                }
            } else {
                console.warn('Dashboard: Not in temporal mode, skipping hazard map update');
            }
        };
        updateHazardMap();
    }, [selectedYear, isTemporal, showAggregated, aggregatedSingleHazard, mapGeojsonLegend, selectedHazardIds, isFetchingLegend]);

    const Loader = () => (
        <div className="loader-container">
            <div className="spinner-border text-primary" role="status">
                <span className="sr-only"></span>
            </div>
        </div>
    );

    const LookUpLoader = () => (
        <div>
            <div className="spinner-border text-primary" role="status" style={{ width: '1rem', height: '1rem' }}>
                <span className="sr-only"></span>
            </div>
        </div>
    );

    const handleBasinSelect = async (selectedList) => {
        if (selectedList) {
            setSelectedBasins(selectedList);
            await getBasinGeojson(selectedList);
        }
    };

    const handleCountrySelect = async (selectedList) => {
        if (selectedList) {
            setSelectedCountries(selectedList);
        }
    };

    useEffect(() => {
        const dropdownHeading = document.querySelector('.countrySelect .dropdown-heading-value');
        if (dropdownHeading) {
            if (selectedCountries.length === countries.length && countries.length > 0) {
                dropdownHeading.textContent = 'All countries are selected';
            } else if (selectedCountries.length > 0) {
                dropdownHeading.textContent = `${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'} selected`;
            } else {
                dropdownHeading.textContent = 'Select countries';
            }
        }
    }, [selectedCountries, countries]);

    useEffect(() => {
        const dropdownHeading = document.querySelector('.basinSelect .dropdown-heading-value');
        if (dropdownHeading) {
            if (selectedBasins.length === riverBasins.length && riverBasins.length > 0) {
                dropdownHeading.textContent = 'All basins are selected';
            } else if (selectedBasins.length > 0) {
                dropdownHeading.textContent = `${selectedBasins.length} ${selectedBasins.length === 1 ? 'basin' : 'basins'} selected`;
            } else {
                dropdownHeading.textContent = 'Select basins';
            }
        }
    }, [selectedBasins, riverBasins]);

    const openAnalysisTab = () => {
        if (loading) return;
        setCurrentTab('analysis');
    };

    return (
        <>
            <nav className="navbar navbar-expand-lg navbar-light bg-light fixed-top p-1">
                <div className="container-fluid px-0">
                    <Link className="navbar-brand" to="/">
                        <img src="/assets/images/logo-header.svg" alt="Logo" />
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#navbarText"
                        aria-controls="navbarText"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse pl-52px" id="navbarText">
                        <b>Product:</b>
                        <ul className="navbar-nav me-3 ms-2">
                            <li className="nav-item">
                                <select
                                    className="form-select formText"
                                    value={selectedProduct}
                                    onChange={(e) => handleProductSelect(e.target.value)}
                                >
                                    <option value="product1">Multi Hazard Mapping</option>
                                    <option value="product2">Vulnerability and Risk Analysis</option>
                                    <option value="product3">Flood Forecasting and Early Warning</option>
                                    <option value="product4">Flood and Drought Monitor</option>
                                </select>
                            </li>
                        </ul>

                        <ul className="nav nav-tabs dash_one_tab" id="myTab" role="tablist">
                            <li className="nav-item" role="presentation" >

                                <button
                                    className={`nav-link layersBox text-white ${currentTab === 'layer' ? 'active' : ''}`}
                                    id="layer-tab"
                                    data-bs-toggle="tab"
                                    data-bs-target="#layer"
                                    type="button"
                                    role="tab"
                                    aria-controls="layer"
                                    aria-selected={currentTab === 'layer'}
                                    onClick={() => {
                                        setCurrentTab('layer');
                                        setShowRivers(showRivers);
                                        setActiveTab(1);
                                    }}

                                >
                                    <span>
                                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1.8335 15.5833L11.0002 20.1667L20.1668 15.5833M1.8335 11L11.0002 15.5833L20.1668 11M11.0002 1.83334L1.8335 6.41668L11.0002 11L20.1668 6.41668L11.0002 1.83334Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span> <span className="ms-2">Layers</span>
                                </button>
                            </li>
                            {/* <li className="nav-item" role="presentation">
                                <button
                                    className={`nav-link ${currentTab === 'analysis' ? 'active' : ''}`}
                                    id="analysis-tab"
                                    data-bs-toggle="tab"
                                    data-bs-target="#analysis"
                                    type="button"
                                    role="tab"
                                    aria-controls="analysis"
                                    aria-selected={currentTab === 'analysis'}
                                    onClick={() => {
                                        openAnalysisTab();
                                        setActiveTab(1);
                                    }}
                                    disabled={loading}
                                >
                                    <span>
                                        <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.5 18.3333V9.16666M17 18.3333V3.66666M6 18.3333V14.6667" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span> Analysis
                                </button>
                            </li> */}
                        </ul>

                        <ul className="list-group ms-auto navbar-nav d-flex flex-row align-items-center">
                            <li className="nav-item dropdown me-3">
                                <a
                                    className="nav-link d-flex align-items-center"
                                    href="#"
                                    id="moreOptionsDropdown"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <i className="bi bi-three-dots-vertical me-1"></i> More Options
                                </a>
                                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="moreOptionsDropdown">
                                    <li>
                                        <a
                                            className={`dropdown-item ${activeTab === 4 ? 'active' : ''}`}
                                            href="#"
                                            onClick={() => handleTabClick(4)}
                                        >
                                            <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.83266 8.25001C9.04817 7.63737 9.47355 7.12077 10.0335 6.79172C10.5934 6.46266 11.2517 6.34237 11.8917 6.45216C12.5318 6.56196 13.1124 6.89474 13.5306 7.39158C13.9489 7.88841 14.1778 8.51724 14.1768 9.16668C14.1768 11 11.4268 11.9167 11.4268 11.9167M11.5002 15.5833H11.5093M20.6668 11C20.6668 16.0626 16.5628 20.1667 11.5002 20.1667C6.43755 20.1667 2.3335 16.0626 2.3335 11C2.3335 5.9374 6.43755 1.83334 11.5002 1.83334C16.5628 1.83334 20.6668 5.9374 20.6668 11Z" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg> Help/Info
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            className={`dropdown-item ${activeTab === 5 ? 'active' : ''}`}
                                            href="#"
                                            onClick={() => handleTabClick(5)}
                                        >
                                            <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.75 13.75V17.4167C19.75 17.9029 19.5568 18.3692 19.213 18.713C18.8692 19.0568 18.4029 19.25 17.9167 19.25H5.08333C4.5971 19.25 4.13079 19.0568 3.78697 18.713C3.44315 18.3692 3.25 17.9029 3.25 17.4167V13.75M6.91667 9.16667L11.5 13.75M11.5 13.75L16.0833 9.16667M11.5 13.75V2.75" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg> Export/Download
                                        </a>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <Link className="btn btn-contact me-1" to="/" style={{ width: '45px' }}>
                                    <i className="bi bi-house fs-6"></i>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="tab-content" id="myTabContent" style={{ height: '100%' }}>
                <div
                    className="help-info-container"
                    style={{ display: activeTab === 4 ? 'block' : 'none' }}
                >
                    <DashboardHelpInfoTab key="help-info" />
                </div>
                <div
                    className="export-download-container"
                    style={{ display: activeTab === 5 ? 'block' : 'none' }}
                >
                    <DashboardExportTab key="export-download" />
                </div>
                <div
                    className={`tab-pane ${currentTab === 'layer' ? 'show active' : ''}`}
                    id="layer"
                    role="tabpanel"
                    aria-labelledby="layer-tab"
                    style={{ display: activeTab === 1 && currentTab === 'layer' ? 'block' : 'none' }}
                >
                    <div className="container-fluid d-flex px-0">
                        <div className={`sidenav p1test ${isOpen ? 'open' : 'collapsed'}`} style={{ padding: '0px' }}>
                            <button className="toggle-btn newBtn" onClick={toggleSidenav}>
                                <img
                                    src={isOpen ? '../assets/images/Layout.png' : '../assets/images/Layout.png'}
                                    alt="Toggle Sidenav"
                                    className="toggle-icon"
                                />
                            </button>
                            {isOpen ? (
                                <div className="card border-0" style={{ marginTop: '3px', height: '605px', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}>
                                    <div className="card-body p-0">
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 col-lg-12 px-1">
                                                <div className="accordian-filter">
                                                    <Accordion defaultActiveKey={['0']} alwaysOpen>
                                                        <Accordion.Item eventKey="0">
                                                            <Accordion.Body className="pt-0 p-2">
                                                                <div className="d-flex justify-content-between align-items-center p-2 pt-3 filterBox">
                                                                    <div><h4>Filters</h4></div>
                                                                    <div><small>Clear (1)</small></div>
                                                                </div>
                                                                <div className="bg-light p-2">
                                                                    <div className="form-group ">
                                                                        <label className="mb-2"><span className="me-1"><img src="../assets/images/country.png" /></span>Country</label>
                                                                        {lookUpLoading ? (
                                                                            <LookUpLoader />
                                                                        ) : (
                                                                            <MultiSelect
                                                                                className="countrySelect"
                                                                                options={countries.map((country) => ({
                                                                                    value: country.adm0_code,
                                                                                    label: country.adm0_name,
                                                                                }))}
                                                                                value={selectedCountries}
                                                                                onChange={handleCountrySelect}
                                                                                labelledBy="Select countries"
                                                                                overrideStrings={{
                                                                                    allItemsAreSelected: 'All countries are selected',
                                                                                    selectSomeItems: 'Select countries'
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="row" style={{ padding: '10px' }}>

                                                                    <div className="col-sm-12 col-md-12 col-lg-12">
                                                                        <div className="form-group">
                                                                            <label>River basins</label>
                                                                            <input
                                                                                className="form-check-input ms-2 mb-1"
                                                                                type="checkbox"
                                                                                checked={showRiverBasin}
                                                                                onChange={() => setShowRiverBasin(!showRiverBasin)}
                                                                                style={{ marginTop: '6px' }}
                                                                            />
                                                                            {lookUpLoading ? (
                                                                                <LookUpLoader />
                                                                            ) : (
                                                                                <MultiSelect
                                                                                    className="basinSelect"
                                                                                    options={riverBasins.map((basin) => ({
                                                                                        value: basin.basin_code,
                                                                                        label: basin.basin,
                                                                                    }))}
                                                                                    value={selectedBasins}
                                                                                    onChange={handleBasinSelect}
                                                                                    labelledBy="Select basins"
                                                                                    overrideStrings={{
                                                                                        allItemsAreSelected: 'All basins are selected',
                                                                                        selectSomeItems: 'Select basins'
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div className="form-group">
                                                                            <label>Rivers</label>
                                                                            <input
                                                                                className="form-check-input ms-2 mb-1"
                                                                                type="checkbox"
                                                                                checked={showRivers}
                                                                                onChange={() => setShowRivers(!showRivers)}
                                                                                style={{ marginTop: '6px' }}
                                                                            />
                                                                        </div>
                                                                        <div className="form-group">
                                                                            <div className="d-flex justify-content-between">
                                                                                <div>
                                                                                    <label>Hazards</label>
                                                                                </div>
                                                                            </div>
                                                                            {lookUpLoading ? (
                                                                                <LookUpLoader />
                                                                            ) : (
                                                                                <>
                                                                                    {hazards.map((hazard) => (
                                                                                        <div className="form-check" key={hazard.hazard_id}>
                                                                                            <input
                                                                                                className="form-check-input"
                                                                                                type="checkbox"
                                                                                                id={`hazard-${hazard.hazard_id}`}
                                                                                                checked={selectedHazardIds.includes(hazard.hazard_id)}
                                                                                                onChange={(e) => handleCheckboxChange(e, hazard.hazard_id)}
                                                                                            />
                                                                                            <label className="form-check-label" htmlFor={`hazard-${hazard.hazard_id}`}>
                                                                                                {hazard.hazard}
                                                                                            </label>
                                                                                        </div>
                                                                                    ))}
                                                                                    <button type="button" className="btn btn-apply w-100 mt-2" onClick={handleApply}>
                                                                                        {loading ? 'Wait...' : 'Apply'}
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    </Accordion>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <img src="../assets/images/filters.svg" alt="Collapsed Icon" className="collapsed-icon" style={{ marginLeft: '10px', marginTop: '15px' }} />
                            )}
                        </div>
                        <div className="rightContent mt-59px">
                            <div className="row">
                                <div className="col-sm-12 col-md-12 col-lg-12 pe-0">
                                    <div className="w-100">
                                        <TestMap
                                            selectedHazardMap={selectedHazardMap}
                                            secondaryGeoJson={riverGeoJson}
                                            showHazard={showHazard}
                                            showRiverBasin={showRiverBasin}
                                            showRivers={showRivers}
                                            years={years}
                                            isTemporal={timebar}
                                            onSliderChange={handleSliderValueChange}
                                            timeLineToggle={isOpen ? '' : ' full'}
                                            setShowAggregated={setShowAggregated}
                                            showAggregated={showAggregated}
                                            selectedCountries={mapSelectedCountries}
                                            geoJsonData={mapGeoJsonData}
                                        />
                                    </div>
                                    <div className="card legendCard border-0" style={{ position: 'absolute', right: '0px', top: '65px', height: 'max-content', }}>
                                        <button className="toggle-button p-2" style={{ background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            <img
                                                src="../assets/images/analytics.png"
                                                alt=""
                                                className="toggle-icon"
                                            /> Analytics
                                        </button>
                                    </div>
                                    <div className="card legendCard border-0" style={{ position: 'absolute', right: '0px', top: '105px', height: 'max-content' }}>
                                        <div className="card-header px-2 py-1 border-0 d-flex mb-0 align-items-center">
                                            <div>
                                                <button onClick={toggleContent} className="toggle-button" style={{ background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                    <img
                                                        src={isExpanded ? collapseIcon : expandIcon}
                                                        alt={isExpanded ? 'Collapse' : 'Expand'}
                                                        className="toggle-icon"
                                                    /> About
                                                </button>
                                            </div>
                                        </div>
                                        {isExpanded && !isTemporal && (
                                            <div className="card-content aboutBoxText p-3 pt-0" style={{ width: '472px' }}>
                                                <h5>Multi-Hazard Mapping for Climate Adaptation in Africa</h5>
                                                <p>Multi-Hazard Mapping consolidates data on natural disasters such as droughts, floods, epidemics, and storms to improve climate adaptation efforts across Africa. This dashboard provides insights into hazard occurrences from 1950 to 2020, based on EM-DAT and FloodList data, spatially disaggregated at the sub-national level for enhanced precision.</p>
                                                <p className="mb-0">Floods and droughts are the most frequent hazards, significantly affecting population exposure and economic losses. Droughts impact 80% of Africa’s population, with Ethiopia, Kenya, and Niger being the most affected. Floods follow, affecting 16% of the population, particularly in Nigeria and Mozambique. Economic damages from floods account for 40% of reported losses, while storms and droughts contribute 31% and 27%, respectively. This detailed hazard mapping enables proactive risk management, ensuring targeted interventions and resource allocation. It supports policymakers, disaster agencies, and humanitarian organizations in implementing evidence-based strategies for disaster resilience, climate adaptation, and sustainable development across Africa.</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="card cardLayers" style={{
                                        backgroundImage: `url(${bgImage})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        fontSize: '12px!important',
                                        fontWeight: '600!important',
                                        position: 'fixed',
                                        bottom: '10px',
                                        left: '300px',
                                        backgroundColor: 'transparent'
                                    }}>
                                        <p className="mb-0 text-white"><span><img src="../assets/images/layers1.png" /></span> Layers</p>
                                    </div>

                                    <div className="card cardLayersMap  p-1 bg-white">
                                        <div><button className="btn text-center">
                                            <span><img src="../assets/images/TerrainMap.png" height={34} /></span> <br />
                                            <span>Terrain</span>
                                        </button></div>
                                        <div><button className="btn text-center">
                                            <span><img src="../assets/images/StreetMap.png" height={34} /></span> <br />
                                            <span>Street Map</span>
                                        </button></div>
                                        <div><button className="btn text-center">
                                            <span><img src="../assets/images/SatellitesMap.png" height={34} /></span> <br />
                                            <span>Satellite</span>
                                        </button></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={`tab-pane ${currentTab === 'analysis' ? 'show active' : ''}`}
                    id="analysis"
                    role="tabpanel"
                    aria-labelledby="analysis-tab"
                    style={{ display: activeTab === 1 && currentTab === 'analysis' ? 'block' : 'none' }}
                >
                    <DashboardAnalysisTabLatest />
                </div>
            </div>

            <div className="modal-container">
                <Modal show={hazardLimit} onHide={() => setHazardLimit(false)}
                    size="lg"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered>
                    <Modal.Body className="p-0">
                        <div className="row">
                            <div className="col-sm-12 col-md-12 col-lg-12">
                                <div className="modal-box">
                                    <div className="modal-title">
                                        <h3>Please select a maximum of four hazards.</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        </>
    );
}

export default Dashboard;